import { Button } from "@freightsimple/ui-core/Controls/Button";
import { KeyValueCell } from "@freightsimple/ui-core/Cells/KeyValueCell";
import { Panel } from "@freightsimple/ui-core/PageLayout/Panel";
import { StackedTextCell } from "@freightsimple/ui-core/Cells/StackedTextCell";
import { Stack } from "@freightsimple/ui-core/ComponentLayout/Stack";
import { Table, type Column } from "@freightsimple/ui-core/Table/Table";
import { TextCell } from "@freightsimple/ui-core/Cells/TextCell";
import { TimestampCell } from "@freightsimple/ui-core/Cells/TimestampCell";
import { asOffsetDateTime } from "@freightsimple/time/branded-types";
import { compareOffsetDateTimes } from "@freightsimple/time/helpers";
import { CarrierIssueType } from "@freightsimple/generated-carriers-openapi-client/models/CarrierIssueType";
import { type OperationalIssue } from "@freightsimple/generated-carriers-openapi-client/models/OperationalIssue";
import { describeIssueType, issueAccent } from "@/App/Views/carrierFormatting";
import { shipmentHref } from "@/App/carrierRoutes";

// Fixed widths mirroring hubble's operational-tasks tables so every panel
// lines up column-for-column.
const W_LOCATION = "200px";
const W_OPEN_FOR = "110px";
const W_REF = "150px";
const W_ALERTS = "200px";

// Which leg of the shipment is most relevant per issue type — drives the
// single contextual column, same as hubble's operational-tasks page. Pickup
// is the default (pre-shipment workflow); in-transit/delivery-side issues
// show the delivery leg instead.
const DELIVERY_CONTEXT_TYPES = new Set<CarrierIssueType>([
  CarrierIssueType.ExpectedDeliveryIsInThePast,
  CarrierIssueType.TrackingNotUpdated,
  CarrierIssueType.MissedDelivery,
  CarrierIssueType.MissingFreight,
  CarrierIssueType.PendingCustomsClearance,
]);

function columnsFor(type: CarrierIssueType): Column<OperationalIssue>[] {
  const deliveryLeg = DELIVERY_CONTEXT_TYPES.has(type);
  return [
    {
      key: "location",
      header: deliveryLeg ? "Delivery" : "Pickup",
      width: W_LOCATION,
      render: (issue) => (
        <StackedTextCell
          primary={deliveryLeg ? issue.delivery : issue.pickup}
          secondary={
            deliveryLeg ? issue.deliveryBusinessName : issue.pickupBusinessName
          }
        />
      ),
    },
    {
      key: "openFor",
      header: "Open For",
      width: W_OPEN_FOR,
      sortable: (a, b) =>
        compareOffsetDateTimes(
          a.openedAt ? asOffsetDateTime(a.openedAt) : undefined,
          b.openedAt ? asOffsetDateTime(b.openedAt) : undefined,
        ),
      render: (issue) => (
        <TimestampCell
          value={issue.openedAt ? asOffsetDateTime(issue.openedAt) : undefined}
          relativeOnly="age"
          relativeBadgeSize="wide"
          relativeNoSuffix
        />
      ),
    },
    {
      key: "ref",
      header: "Ref",
      width: W_REF,
      render: (issue) => (
        <KeyValueCell
          items={[
            { key: "Pro #", value: issue.proNumber ?? undefined },
            { key: "BOL", value: issue.billOfLadingNumber ?? undefined },
          ]}
        />
      ),
    },
    {
      key: "alerts",
      header: "Alerts",
      width: W_ALERTS,
      render: (issue) => <TextCell value={issue.title ?? undefined} />,
    },
  ];
}

// Operational issues grouped by type into accented, collapsible panels — the
// same composition and column order hubble's operational-tasks page uses,
// minus the internal-only columns (carrier, company, assignee, conversation
// status).
export function OperationalIssuesPanels({
  issues,
}: {
  issues: OperationalIssue[];
}) {
  const groups = Object.values(CarrierIssueType)
    .map((type) => ({
      type,
      rows: issues.filter((issue) => issue.issueType === type),
    }))
    .filter((group) => group.rows.length > 0);

  return (
    <Stack gap={4}>
      {groups.map((group) => (
        <Panel
          key={group.type}
          collapsible
          fullBleed
          accent={issueAccent(group.type)}
          title={describeIssueType(group.type)}
          count={group.rows.length}
        >
          <Table
            columns={columnsFor(group.type)}
            data={group.rows}
            rowKey={(issue) => issue.shipmentId}
            defaultSortKey="openFor"
            defaultSortDir="asc"
            rowActions={(issue) => (
              <Button href={shipmentHref(issue.shipmentId)}>View</Button>
            )}
          />
        </Panel>
      ))}
    </Stack>
  );
}
